import format from 'string-format';
import { extendObservable, action } from 'mobx';
import firebase from 'firebase';

import WarningStrings from '../strings/en/warning';
import Property from '../models/Property';

const PropertiesRef = firebase.database().ref("/properties");

class PropertyStore {
  constructor() {
    extendObservable(this, {
      userId: "",
      selectedProperty: null,
      properties: [],
    });
  }

  getPropertyById = (propertyId) => {
    return this.properties.find((property) => property.id === propertyId);
  }

  add = (property) => {
    // make sure this property doesn't exists yet
    if (this.getPropertyById(property.id)) {
      throw format(WarningStrings.PropertyExists, property.id);
    }
    // update db copy
    const ref = PropertiesRef.child(this.userId);
    property.set({
      id: ref.push().key,
      userId: this.userId,
    });
    let data = {};
    data[property.id] = property.get();
    return ref.update(data).then(action(() => {
      this.properties.push(property);
      this.selectedProperty = property;

      this.waitFor(property.id, "twilioNumber").then((twilioNumber) => {
        property.set({ twilioNumber: twilioNumber });
      });

      return property;
    }));
  };

  update = (property) => {
    // make sure this property exists
    let prop = this.getPropertyById(property.id);
    if (!prop) {
      throw format(WarningStrings.PropertyNotExists, property.id);
    }
    // update db copy
    const ref = PropertiesRef.child(this.userId);
    let data = {};
    data[prop.id] = property.get();
    return ref.update(data).then(action(() => {
      prop.set(property.get());
      return prop;
    }));
  };

  delete = (propertyId) => {
    // make sure this property exists
    let property = this.getPropertyById(propertyId);
    if (!property) {
      throw format(WarningStrings.PropertyNotExists, propertyId);
    }
    const ref = PropertiesRef.child(`${this.userId}/${propertyId}`);
    return ref.remove().then(action(() => {
      const index = this.properties.indexOf(property);
      if (index !== -1) {
        this.properties.splice(index, 1);
      }
      // set the first property as the selected property
      if (this.properties && this.properties.length) {
        const firstProperty = this.properties[0];
        this.setSelectedProperty(firstProperty.id);
      } else {
        this.clearSelectedProperty();
      }
    }));
  }

  waitFor = (propertyId, key) => {
    return new Promise((resolve, reject) => {
      const ref = PropertiesRef.child(`${this.userId}/${propertyId}/${key}`);
      const listener = ref.on("value", (snapshot) => {
        const value = snapshot.val();
        if (value) {
          ref.off("value", listener);
          const property = this.getPropertyById(propertyId);
          let data = {};
          data[key] = value;
          property.set(data);
          resolve(value);
        }
      });
    });
  }

  setUser = (userId) => {
    this.userId = userId;
    return this.getUserProperties(userId)
              .then(action((properties) => {
                this.properties = properties;
                this.selectedProperty = this.properties[0];
              }));
  };

  clearUser = action(() => {
    this.userId = "";
    this.properties.replace([]);
    this.selectedProperty = null;
  });

  setSelectedProperty = action((propertyId) => {
    const property = this.getPropertyById(propertyId);
    if (!property) {
      throw format(WarningStrings.PropertyNotExists, propertyId);
    }

    this.selectedProperty = property;
    return this.selectedProperty;
  });

  clearSelectedProperty = action(() => {
    this.selectedProperty = null;
  })

  getUserProperties = (userId) => {
    return PropertiesRef.child(userId).once("value")
            .then((snapshots) => {
              let ret = [];
              snapshots.forEach((snapshot) => {
                ret.push(new Property(snapshot.val()));
              });
              return ret;
            });
  }
}

const propertyStore = new PropertyStore();

export default propertyStore;
export { PropertyStore }; // why?


// WEBPACK FOOTER //
// ./src/stores/PropertyStore.js